// ─── Storage Service ──────────────────────────────────────────────────────────
// Cloud save/load for user trips via Supabase.
// Falls back gracefully when user is not signed in (caller uses localStorage).

import { supabase } from "./supabaseClient";
import type { City, TripLeg, StopActions } from "../data/types";
import type { Json } from "../data/database.types";
import { reportError } from "./errorService";
import { toast } from "sonner";
import { POI_CONFIG } from "./poiService";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Lightweight summary returned by listTrips (no full JSON blobs). */
export interface SavedTripSummary {
  id: string;
  name: string;
  travelDate: string | null;
  updatedAt: string;
  stopCount: number;
}

/** Full saved trip record returned by loadTrip. */
export interface SavedTripFull {
  id: string;
  name: string;
  travelDate: string | null;
  stops: City[];
  stopActions: StopActions;
  legs: TripLeg[];
  shareToken: string;
  updatedAt: string;
  createdAt: string;
}

// ─── Save / Upsert ────────────────────────────────────────────────────────────

/**
 * Save a trip to the cloud. If `tripId` is provided, updates that row;
 * otherwise inserts a new one and returns the new id + share token.
 */
export async function saveTrip(
  userId: string,
  name: string,
  travelDate: string,
  stops: City[],
  stopActions: StopActions,
  legs: TripLeg[] = [],
  tripId?: string
): Promise<{ id: string; shareToken: string } | null> {

  let snapshot: ReturnType<typeof parseTripSnapshot>;
  try {
    snapshot = parseTripSnapshot({ tripName: name, travelDate, stops, stopActions, legs });
  } catch {
    toast.error("This trip contains invalid data and could not be saved.");
    return null;
  }
  const payload = {
    user_id: userId,
    name: snapshot.tripName,
    travel_date: snapshot.travelDate,
    stops: snapshot.stops as unknown as Json,
    stop_actions: snapshot.stopActions as unknown as Json,
    legs: snapshot.legs as unknown as Json,
    updated_at: new Date().toISOString(),
  };

  if (tripId) {
    const { data, error } = await supabase
      .from("saved_trips")
      .update(payload)
      .eq("id", tripId)
      .select("id, share_token")
      .single();

    if (error) {
      reportError(error, { level: "critical", context: { op: "updateTrip", tripId } });
      toast.error("Failed to save trip — please try again.");
      return null;
    }
    return { id: data.id, shareToken: data.share_token };
  }

  const { data, error } = await supabase
    .from("saved_trips")
    .insert(payload)
    .select("id, share_token")
    .single();

  if (error) {
    reportError(error, { level: "critical", context: { op: "insertTrip" } });
    toast.error("Failed to save trip — please try again.");
    return null;
  }
  return { id: data.id, shareToken: data.share_token };
}

// ─── List / Load / Delete / Rename ────────────────────────────────────────────

/**
 * List all trips for a user (summary only — no full JSON blobs).
 * Ordered by most recently updated first.
 */
export async function listTrips(userId: string): Promise<SavedTripSummary[]> {
  const { data, error } = await supabase
    .from("saved_trips")
    .select("id, name, travel_date, updated_at, stops")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });

  if (error) {
    reportError(error, { level: "critical", context: { op: "listTrips" } });
    return [];
  }

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    travelDate: row.travel_date,
    updatedAt: row.updated_at,
    stopCount: Array.isArray(row.stops) ? row.stops.length : 0,
  }));
}

/**
 * Load a trip by its public share token — no auth required.
 * Uses a SECURITY DEFINER function to bypass RLS safely.
 */
export async function loadTripByShareToken(token: string): Promise<SavedTripFull | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc("get_shared_trip", { p_token: token });

  if (error || !data || data.length === 0) {
    if (error) reportError(error, { level: "info", context: { op: "loadTripByShareToken" } });
    return null;
  }

  const row = data[0];
  try {
    const snapshot = parseTripSnapshot({ stops: row.stops, tripName: row.name,
      travelDate: row.travel_date || new Date().toISOString().slice(0, 10), stopActions: row.stop_actions, legs: row.legs });
    return {
      id: row.id,
    name: row.name,
    travelDate: row.travel_date,
    stops: snapshot.stops,
    stopActions: snapshot.stopActions,
    legs: snapshot.legs,
    shareToken: row.share_token,
    updatedAt: row.updated_at,
    createdAt: row.created_at,
  };
  } catch {
    toast.error("This trip contains invalid data and could not be loaded.");
    return null;
  }
}

/**
 * Load a single trip with all data (stops, legs, actions).
 * RLS ensures the caller can only load their own trips.
 */
export async function loadTrip(tripId: string): Promise<SavedTripFull | null> {
  const { data, error } = await supabase
    .from("saved_trips")
    .select("*")
    .eq("id", tripId)
    .single();

  if (error) {
    reportError(error, { level: "critical", context: { op: "loadTrip", tripId } });
    toast.error("Failed to load trip — please try again.");
    return null;
  }

  try {
    const snapshot = parseTripSnapshot({ stops: data.stops, tripName: data.name,
      travelDate: data.travel_date || new Date().toISOString().slice(0, 10), stopActions: data.stop_actions, legs: data.legs });
    return {
      id: data.id,
    name: data.name,
    travelDate: data.travel_date,
    stops: snapshot.stops,
    stopActions: snapshot.stopActions,
    legs: snapshot.legs,
    shareToken: data.share_token,
    updatedAt: data.updated_at,
    createdAt: data.created_at,
  };
  } catch {
    toast.error("This trip contains invalid data and could not be loaded.");
    return null;
  }
}

/**
 * Delete a saved trip. RLS ensures ownership.
 * Returns true on success.
 */
export async function deleteTrip(tripId: string): Promise<boolean> {
  const { error } = await supabase
    .from("saved_trips")
    .delete()
    .eq("id", tripId);

  if (error) {
    reportError(error, { level: "critical", context: { op: "deleteTrip", tripId } });
    toast.error("Failed to delete trip — please try again.");
    return false;
  }
  return true;
}

/**
 * Rename a saved trip. RLS ensures ownership.
 */
export async function renameTrip(tripId: string, name: string): Promise<boolean> {
  const { error } = await supabase
    .from("saved_trips")
    .update({ name, updated_at: new Date().toISOString() })
    .eq("id", tripId);

  if (error) {
    reportError(error, { level: "info", context: { op: "renameTrip", tripId } });
    return false;
  }
  return true;
}

/** Shared boundary for cloud, local draft and URL JSON. Never trust serialized casts. */
export function parseTripSnapshot(value: unknown): {
  stops: City[]; tripName: string; travelDate: string; stopActions: StopActions; legs: TripLeg[];
} {
  const record = (value: unknown): Record<string, unknown> => {
    if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid trip data");
    return value as Record<string, unknown>;
  };
  const string = (value: unknown, max = 200) => {
    if (typeof value !== "string" || value.length > max) throw new Error("Invalid trip text");
    return value;
  };
  const coord = (value: unknown, bound: number) => {
    if (typeof value !== "number" || !Number.isFinite(value) || Math.abs(value) > bound) throw new Error("Invalid trip coordinates");
    return value;
  };
  const city = (value: unknown): City => {
    const v = record(value);
    const id = string(v.id);
    if (!id || ["__proto__", "prototype", "constructor"].includes(id)) throw new Error("Invalid city ID");
    const result: City = { id, name: string(v.name), city: string(v.city), country: string(v.country ?? ""),
      latitude: coord(v.latitude, 90), longitude: coord(v.longitude, 180),
      population: typeof v.population === "number" && Number.isFinite(v.population) ? Math.max(0, v.population) : 0 };
    if (v.poiType != null) {
      const poiType = string(v.poiType);
      if (!Object.hasOwn(POI_CONFIG, poiType)) throw new Error("Invalid point of interest type");
      result.poiType = poiType;
    }
    for (const key of ["hafasId", "poiName", "poiDescription", "poiReason", "poiShowcaseText", "spotifyUri", "description"] as const) {
      if (v[key] != null) result[key] = string(v[key], 10000);
    }
    if (typeof v.imageUrl === "string" && /^https?:\/\//i.test(v.imageUrl)) result.imageUrl = string(v.imageUrl, 4000);
    for (const key of ["attractions", "foodSpecialties"] as const) {
      if (Array.isArray(v[key])) result[key] = (v[key] as unknown[]).slice(0, 100).map(x => string(x, 2000));
    }
    return result;
  };
  const v = record(value);
  if (!Array.isArray(v.stops) || v.stops.length > 100) throw new Error("Invalid trip stops");
  const stops = v.stops.map(city);
  if (new Set(stops.map(s => s.id)).size !== stops.length) throw new Error("Duplicate trip stops");
  const rawTravelDate = string(v.travelDate, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(rawTravelDate)) throw new Error("Invalid travel date");
  const travelDate = new Date(`${rawTravelDate}T12:00:00Z`).toISOString().slice(0, 10);
  if (travelDate !== rawTravelDate) throw new Error("Invalid travel date");
  const stopActions: StopActions = {};
  const rawActions = record(v.stopActions ?? {});
  for (const stop of stops) {
    if (!Object.hasOwn(rawActions, stop.id)) continue;
    const a = record(rawActions[stop.id]);
    if (a.nights != null && (typeof a.nights !== "number" || !Number.isInteger(a.nights) || a.nights < 1 || a.nights > 365)) throw new Error("Invalid overnight stay");
    stopActions[stop.id] = { food: a.food === true, overnight: a.overnight === true, fika: a.fika === true,
      ...(a.nights != null ? { nights: a.nights as number } : {}) };
  }
  if (v.legs != null && (!Array.isArray(v.legs) || v.legs.length > 100)) throw new Error("Invalid trip legs");
  const legDates = (l: Record<string, unknown>) => {
    const dates: Pick<TripLeg, "departureDate" | "arrivalDate" | "departureInstant" | "arrivalInstant"> = {};
    for (const key of ["departureDate", "arrivalDate", "departureInstant", "arrivalInstant"] as const) {
      if (l[key] == null) continue;
      const date = string(l[key], 50);
      const valid = key.endsWith("Instant")
        ? /(?:Z|[+-]\d{2}:\d{2})$/.test(date) && Number.isFinite(Date.parse(date))
        : /^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(Date.parse(date)) && new Date(date).toISOString().slice(0, 10) === date;
      if (!valid) throw new Error("Invalid leg date");
      dates[key] = date;
    }
    return dates;
  };
  const legs = ((v.legs ?? []) as unknown[]).map((value): TripLeg => {
    const l = record(value);
    if (!Array.isArray(l.stations) || l.stations.length > 1000) throw new Error("Invalid leg stations");
    const leg: TripLeg = {
      fromStationId: string(l.fromStationId), toStationId: string(l.toStationId),
      departureTime: string(l.departureTime, 5), arrivalTime: string(l.arrivalTime, 5),
      duration: string(l.duration), operator: string(l.operator), routeId: string(l.routeId, 10000),
      stations: l.stations.map(s => string(s)),
      ...legDates(l),
    };
    for (const time of [leg.arrivalTime, leg.departureTime]) if (time && !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)) throw new Error("Invalid train time");
    for (const key of ["isSleeper", "isGhostLeg", "requiresSelection", "ghostError", "ghostLoading", "ghostThrottled"] as const) leg[key] = l[key] === true;
    // Derived day colours and ghost schedule keys are recomputed after import.
    if (l.price) {
      const price = record(l.price);
      if (typeof price.amount === "number" && Number.isFinite(price.amount) && price.amount >= 0) leg.price = { amount: price.amount, currency: string(price.currency, 3) };
    }
    if (Array.isArray(l.routeCoords) && l.routeCoords.length <= 10000) leg.routeCoords = l.routeCoords.map(value => {
      if (!Array.isArray(value) || value.length !== 2) throw new Error("Invalid route geometry");
      return [coord(value[0], 90), coord(value[1], 180)];
    });
    if (Array.isArray(l.stationData) && l.stationData.length <= 1000) leg.stationData = l.stationData.map(value => {
      const data = record(value);
      const station = { ...city(value) } as NonNullable<TripLeg["stationData"]>[number];
      for (const key of ["arrival", "departure", "arrivalInstant", "departureInstant"] as const) {
        if (data[key] == null) continue;
        const time = string(data[key], 50);
        const valid = key.endsWith("Instant")
          ? /(?:Z|[+-]\d{2}:\d{2})$/.test(time) && Number.isFinite(Date.parse(time))
          : !time || /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time);
        if (!valid) throw new Error("Invalid station time");
        station[key] = time;
      }
      return station;
    });
    return leg;
  }).filter(leg => stops.some((s, i) => s.id === leg.fromStationId && stops[i + 1]?.id === leg.toStationId));
  return { stops, tripName: string(v.tripName || "My Trip", 200), travelDate, stopActions, legs };
}
